<template>
  <div class="rv-feedback">
    <!-- Shield banner -->
    <div class="rv-shield">
      <i class="fa fa-shield"></i>
      <div>
        <div class="rv-shield-title">These never went public</div>
        <div class="rv-shield-body">
          Any rating below your public-review bar is gated here instead of Google. Resolve it offline,
          then mark it done — the patient stays private the whole time.
        </div>
      </div>
    </div>

    <!-- Filter row -->
    <div class="rv-fb-filter">
      <button
        v-for="f in filters"
        :key="f.value"
        class="rv-pill"
        :class="{ active: statusFilter === f.value }"
        @click="setFilter(f.value)"
      >{{ f.label }}</button>
    </div>

    <div v-if="loading" class="rv-fb-loading">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
    </div>

    <div v-else-if="!feedback.length" class="rv-card rv-fb-empty">
      <i class="fa fa-shield"></i>
      <div class="rv-fb-empty-title">No private feedback{{ statusFilter ? ' in this view' : '' }}</div>
      <div class="rv-fb-empty-sub">When a patient rates below your gate threshold, their note lands here — and is emailed to your front desk.</div>
    </div>

    <div v-else class="rv-fb-list">
      <div
        v-for="fb in feedback"
        :key="fb.feedbackID"
        class="rv-card rv-fb-card"
        :style="{ borderLeftColor: accentColor(fb) }"
      >
        <div class="rv-fb-head">
          <div class="rv-avatar">{{ initials(fb.patientName) }}</div>
          <div class="rv-fb-meta">
            <div class="rv-fb-name-row">
              <span class="rv-fb-name">{{ fb.patientName || 'Unknown patient' }}</span>
              <span class="rv-stars" v-if="fb.rating">
                <span class="rv-star-full">{{ '★'.repeat(fb.rating) }}</span><span class="rv-star-empty">{{ '☆'.repeat(5 - fb.rating) }}</span>
              </span>
            </div>
            <div class="rv-fb-sub">#{{ fb.patNum }} · {{ formatWhen(fb.createdAt) }}</div>
          </div>
          <span v-if="fb.status === 'resolved'" class="rv-chip rv-chip-resolved"><i class="fa fa-check"></i>Resolved</span>
          <span v-else class="rv-chip rv-chip-open"><i class="fa fa-dot-circle-o"></i>Needs attention</span>
        </div>

        <div class="rv-fb-tags" v-if="fb.reasons && fb.reasons.length">
          <span class="rv-tag" v-for="(t, i) in fb.reasons" :key="i">{{ t }}</span>
        </div>

        <div class="rv-fb-note" v-if="fb.note">{{ fb.note }}</div>
        <div class="rv-fb-note rv-fb-note-empty" v-else>No additional note was left.</div>

        <div class="rv-fb-response" v-if="fb.responseNote">
          <i class="fa fa-reply"></i>
          <div><span class="rv-fb-response-label">Your response</span>{{ fb.responseNote }}</div>
        </div>

        <div class="rv-fb-actions">
          <button class="rv-btn rv-btn-secondary" @click="openRespond(fb)">Respond privately</button>
          <button class="rv-btn rv-btn-ghost" @click="toggleResolved(fb)" :disabled="busyIds.includes(fb.feedbackID)">
            {{ fb.status === 'resolved' ? 'Reopen' : 'Mark resolved' }}
          </button>
          <span class="rv-fb-emailed"><i class="fa fa-envelope-o"></i>Emailed to front desk</span>
        </div>
      </div>
    </div>

    <!-- Respond dialog -->
    <b-modal ref="respond" id="rv-respond" title="Respond privately" body-class="bg-white" hide-footer>
      <p class="rv-modal-sub">
        This note is for your team's records — it is not sent to the patient. Call, email, or text them
        directly to make it right.
      </p>
      <textarea
        v-model="respondNote"
        class="rv-modal-textarea"
        rows="5"
        placeholder="e.g. Called the patient, apologized for the wait, comped their cleaning."
      ></textarea>
      <div class="rv-modal-actions">
        <button class="rv-btn rv-btn-ghost" @click="hideRespond">Cancel</button>
        <button class="rv-btn rv-btn-secondary" @click="saveResponse(false)" :disabled="savingResponse">Save note</button>
        <button class="rv-btn rv-btn-primary" @click="saveResponse(true)" :disabled="savingResponse">Save &amp; mark resolved</button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';

export default {
  name: 'ReviewsPrivateFeedback',
  mixins: [mixin],

  data() {
    return {
      statusFilter: 'open',
      filters: [
        { value: 'open', label: 'Needs attention' },
        { value: 'resolved', label: 'Resolved' },
        { value: '', label: 'All' },
      ],
      respondItem: null,
      respondNote: '',
      savingResponse: false,
      busyIds: [],
    };
  },

  async created() {
    await this.loadFeedback();
  },

  methods: {
    ...mapActions('reviews', ['getPrivateFeedback', 'resolvePrivateFeedback']),
    ...mapMutations('reviews', ['PATCH_FEEDBACK']),

    async loadFeedback() {
      try {
        await this.getPrivateFeedback({ siteID: this.siteID, status: this.statusFilter || undefined });
      } catch (err) {
        console.error('[ReviewsPrivateFeedback] load failed:', err);
        this.$toast && this.$toast.error('Could not load private feedback.');
      }
    },

    setFilter(value) {
      this.statusFilter = value;
      this.loadFeedback();
    },

    openRespond(fb) {
      this.respondItem = fb;
      this.respondNote = fb.responseNote || '';
      this.$refs.respond.show();
    },

    hideRespond() {
      this.$refs.respond.hide();
    },

    async saveResponse(alsoResolve) {
      if (!this.respondItem) return;
      const fb = this.respondItem;
      const newStatus = alsoResolve ? 'resolved' : fb.status;
      this.savingResponse = true;
      try {
        await this.resolvePrivateFeedback({
          siteID: this.siteID,
          feedbackID: fb.feedbackID,
          status: newStatus,
          responseNote: this.respondNote,
          employeeID: this.user && this.user.EmployeeID,
        });
        this.PATCH_FEEDBACK({
          feedbackID: fb.feedbackID,
          patch: {
            responseNote: this.respondNote,
            status: newStatus,
            resolvedBy: newStatus === 'resolved' ? (this.user && this.user.EmployeeID) : null,
            resolvedAt: newStatus === 'resolved' ? new Date().toISOString() : null,
          },
        });
        this.$toast && this.$toast.success(alsoResolve ? 'Response saved and marked resolved.' : 'Response saved.');
        this.hideRespond();
        // If the item no longer matches the active filter, refresh the list.
        if (this.statusFilter && this.statusFilter !== newStatus) this.loadFeedback();
      } catch (err) {
        console.error('[ReviewsPrivateFeedback] saveResponse failed:', err);
        this.$toast && this.$toast.error('Could not save your response.');
      } finally {
        this.savingResponse = false;
      }
    },

    async toggleResolved(fb) {
      const newStatus = fb.status === 'resolved' ? 'open' : 'resolved';
      this.busyIds.push(fb.feedbackID);
      // Optimistic patch.
      this.PATCH_FEEDBACK({
        feedbackID: fb.feedbackID,
        patch: {
          status: newStatus,
          resolvedBy: newStatus === 'resolved' ? (this.user && this.user.EmployeeID) : null,
          resolvedAt: newStatus === 'resolved' ? new Date().toISOString() : null,
        },
      });
      try {
        await this.resolvePrivateFeedback({
          siteID: this.siteID,
          feedbackID: fb.feedbackID,
          status: newStatus,
          employeeID: this.user && this.user.EmployeeID,
        });
        this.$toast && this.$toast.success(newStatus === 'resolved' ? 'Marked resolved.' : 'Reopened.');
        if (this.statusFilter && this.statusFilter !== newStatus) this.loadFeedback();
      } catch (err) {
        console.error('[ReviewsPrivateFeedback] toggleResolved failed:', err);
        // Roll back by reloading the authoritative list.
        this.$toast && this.$toast.error('Could not update this item.');
        this.loadFeedback();
      } finally {
        this.busyIds = this.busyIds.filter((id) => id !== fb.feedbackID);
      }
    },

    accentColor(fb) {
      if (fb.status === 'resolved') return '#0d9488';       // teal
      if (fb.rating != null && fb.rating <= 1) return '#ef4444'; // red
      return '#f59e0b';                                     // amber (2–3)
    },

    initials(name) {
      if (!name) return '?';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('');
    },

    formatWhen(value) {
      if (!value) return '';
      // CreatedAt is a server TIMESTAMP (UTC) — convert UTC → site TZ for display.
      const m = this.timezone ? moment.utc(value).tz(this.timezone) : moment(value);
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : '';
    },
  },

  computed: {
    ...mapState('reviews', {
      feedback: (s) => s.privateFeedback,
      loading: (s) => s.loading.feedback,
    }),
    timezone() {
      return (this.allSettings
        && this.allSettings.GENERAL_TIMEZONE
        && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$primary-subtle: #edfbf6;
$border: #e2e8f0;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.rv-feedback { display: flex; flex-direction: column; gap: 16px; }

.rv-shield {
  display: flex; gap: 13px; align-items: flex-start;
  background: $primary-subtle; border: 1px solid $border; border-radius: 14px; padding: 16px 18px;
  .fa { font-size: 20px; color: $primary; margin-top: 2px; }
}
.rv-shield-title { font-weight: 700; font-size: 14px; color: $neutral-900; }
.rv-shield-body { font-size: 13px; color: #475569; line-height: 1.5; margin-top: 2px; }

.rv-fb-filter { display: inline-flex; background: #f1f5f9; border-radius: 10px; padding: 3px; gap: 2px; align-self: flex-start; }
.rv-pill {
  border: none; cursor: pointer; font-family: inherit;
  font-size: 12.5px; font-weight: 600; padding: 6px 14px; border-radius: 8px;
  background: transparent; color: #475569;
  &.active { background: #fff; color: $neutral-900; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08); }
}

.rv-fb-loading { text-align: center; padding: 40px 0; }
.rv-card { background: #fff; border: 1px solid $border; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); }

.rv-fb-empty { text-align: center; padding: 44px 24px;
  .fa { font-size: 26px; color: $neutral-400; margin-bottom: 10px; }
}
.rv-fb-empty-title { font-weight: 700; color: $neutral-900; }
.rv-fb-empty-sub { font-size: 13px; color: $neutral-500; margin-top: 4px; }

.rv-fb-list { display: flex; flex-direction: column; gap: 14px; }
.rv-fb-card { border-left: 3px solid #f59e0b; padding: 18px 20px; }

.rv-fb-head { display: flex; align-items: center; gap: 12px; }
.rv-avatar {
  width: 38px; height: 38px; border-radius: 999px; flex-shrink: 0;
  background: #e2e8f0; color: #475569; font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.rv-fb-meta { flex: 1; min-width: 0; }
.rv-fb-name-row { display: flex; align-items: center; gap: 9px; }
.rv-fb-name { font-weight: 700; color: $neutral-900; }
.rv-stars { letter-spacing: 1px; font-size: 13px; }
.rv-star-full { color: #f59e0b; }
.rv-star-empty { color: #cbd5e1; }
.rv-fb-sub { font-size: 12px; color: $neutral-400; margin-top: 1px; }

.rv-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; padding: 4px 10px; border-radius: 999px; }
.rv-chip-resolved { color: #0d9488; background: #ecfdf5; }
.rv-chip-open { color: #b45309; background: #fffbeb; }

.rv-fb-tags { display: flex; flex-wrap: wrap; gap: 6px; margin: 13px 0 10px; }
.rv-tag { font-size: 11.5px; font-weight: 600; color: #475569; background: #f1f5f9; padding: 3px 9px; border-radius: 999px; }

.rv-fb-note { color: #334155; line-height: 1.55; font-size: 14px; background: #fafbfb; border-radius: 10px; padding: 12px 14px; }
.rv-fb-note-empty { color: $neutral-400; font-style: italic; }

.rv-fb-response {
  display: flex; gap: 9px; margin-top: 12px; padding: 11px 13px; border-radius: 10px;
  background: $primary-subtle; font-size: 13px; color: #334155; line-height: 1.5;
  .fa { color: $primary; margin-top: 2px; }
}
.rv-fb-response-label { display: block; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: $primary; margin-bottom: 2px; }

.rv-fb-actions { display: flex; align-items: center; gap: 10px; margin-top: 13px; flex-wrap: wrap; }
.rv-fb-emailed { margin-left: auto; font-size: 12px; color: $neutral-400; display: inline-flex; align-items: center; gap: 5px; }

.rv-btn {
  border: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600;
  padding: 8px 16px; border-radius: 9px; transition: background 0.15s ease;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.rv-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #334155; &:not(:disabled):hover { background: #f8fafc; } }
.rv-btn-ghost { background: transparent; color: #475569; &:not(:disabled):hover { background: #f1f5f9; } }
.rv-btn-primary { background: $primary; color: #fff; &:not(:disabled):hover { background: #0b5f58; } }

.rv-modal-sub { font-size: 13px; color: $neutral-500; line-height: 1.5; margin-bottom: 12px; }
.rv-modal-textarea {
  width: 100%; border: 1px solid #cbd5e1; border-radius: 11px; padding: 11px 13px;
  font-family: inherit; font-size: 13.5px; line-height: 1.5; resize: vertical; box-sizing: border-box;
}
.rv-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; flex-wrap: wrap; }
</style>
